import React, { useEffect, memo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format, parseISO } from 'date-fns';
import { Button, Icon, Textarea } from '@iq/react-components';

import { UserAvatar, UserSignature } from '../../utils';
import Loader from '../../../../Loader';
import {
  fetchComments,
  getCommentsByParentId,
  createComment,
  deleteComment,
} from '../../../../../bundles/comments';
import { requestUser, getUser } from '../../../../../bundles/ad';
import { getHasPermission } from '../../../../../bundles/auth';
import { getActiveSite } from '../../../../../bundles/sites';

import './CommentsTab.scss';

const CommentsTab = memo(({ alarm }) => {
  const dispatch = useDispatch();
  const comments = useSelector((state) => getCommentsByParentId(state, alarm.id));
  const me = useSelector((state) => getUser(state, 'me'));
  const activeSite = useSelector(getActiveSite);
  // alarm.org/alarm.site can be stale placeholders on on-prem DTMP alarms, so use the real site
  const scope = { org: activeSite?.org, site: activeSite?.id };
  const canDeleteComments = useSelector((state) =>
    getHasPermission(state, 'comments/Delete', scope)
  );
  const canWriteComments = useSelector((state) => getHasPermission(state, 'comments/Write', scope));
  const [body, setBody] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleFormSubmit = useCallback(() => {
    if (!body) return;

    const commentData = {
      body,
      org: activeSite?.org,
      site: activeSite?.id,
      parentId: alarm.id,
    };
    dispatch(createComment(commentData));
    setBody('');
  }, [body, activeSite?.org, activeSite?.id, alarm.id, dispatch]);

  const handleDelete = async (comment) => {
    setIsDeleting(true);
    try {
      dispatch(deleteComment({ commentId: comment.id, parentId: alarm.id }));
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (alarm && alarm.id) {
      setIsLoading(true);
      dispatch(fetchComments({ parentId: alarm.id }));
    }
  }, [dispatch, alarm]);

  useEffect(() => {
    // When comments are received, stop loading
    setIsLoading(false);
  }, [comments]);

  useEffect(() => {
    if (comments.length > 0) {
      const userIds = [...new Set(comments.map((c) => c.createdBy))];
      userIds.forEach((userId) => {
        dispatch(requestUser(userId));
      });
    }
  }, [dispatch, comments]);

  return (
    <div className="alarm-comment-form">
      {canWriteComments && (
        <div className="alarm-comment-form__input-container">
          <Textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={4}
            placeholder="Add new comment"
          />
          <div className="alarm-comment-form__actions">
            <Button
              activity="primary"
              onClick={handleFormSubmit}
              disabled={!body}
            >
              Comment
            </Button>
          </div>
        </div>
      )}
      <div className="alarm-comment-form__comments">
        {isLoading && comments.length === 0 ? (
          <Loader />
        ) : comments.length === 0 ? (
          <p className="alarm-comment-form__empty">No comments</p>
        ) : (
          comments.map((comment) => (
            <div
              key={comment.id}
              className="alarm-comment-form__comment"
            >
              <div className="alarm-comment-form__badge">
                <UserAvatar userId={comment.createdBy} />
              </div>
              <div className="alarm-comment-form__content">
                <div className="alarm-comment-form__byline">
                  <div className="alarm-comment-form__created-by">
                    <UserSignature userId={comment.createdBy} />
                  </div>
                  <div className="alarm-comment-form__created-at">
                    {format(parseISO(comment.createdAt), 'd MMM')}
                  </div>
                </div>
                <div className="alarm-comment-form__body">
                  {comment.body}
                  {(canDeleteComments || comment.createdBy === (me && me.id)) && (
                    <Button
                      disabled={isDeleting}
                      className="alarm-comment-form__comment-delete"
                      slim
                      activity="secondary"
                      design="text"
                      onClick={() => handleDelete(comment)}
                      icon={<Icon icon="he-delete" />}
                    />
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
});

export default CommentsTab;
